import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useState} from 'react';
import {heatMapGradient} from '../util/styles.js';

const LegendPanel = styled(Paper)(({theme}) => ({
  background: 'transparent',
  color: '#fff',
  padding: theme.spacing(0.5),
  zIndex: theme.zIndex.appBar + 1,
}));

const PopperPanel = styled(Paper)({
  backgroundColor: '#000',
});

const LegendItem = styled('div')(({color}) => ({
  width: 20,
  height: 20,
  backgroundColor: color,
  margin: 1,
}));

const StyledBox = styled(Box)({
  cursor: 'pointer',
});

const LessText = styled(Typography)({
  flex: 1,
});

/**
 * This shows a color ramp for the selected heatmap color ramp. It also allows
 * changing the color ramp by using a Popover with the available color ramps.
 */
const HeatMapLegend = ({gradient, onClick}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = useCallback(
    event => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl],
  );

  const handleClickAway = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const selectRamp = idx => () => {
    onClick(idx);
  };

  return (
    <LegendPanel elevation={0}>
      <Tooltip title="Click to change heatmap gradient colors">
        <StyledBox
          display="flex"
          flexDirection="row"
          onClick={handleClick}
          sx={anchorEl ? {visibility: 'hidden'} : {}}
        >
          {gradient.map(color => {
            return <LegendItem key={color} color={color} />;
          })}
        </StyledBox>
      </Tooltip>
      <Box
        display="flex"
        flexDirection="row"
        sx={anchorEl ? {visibility: 'hidden'} : {}}
      >
        <LessText variant="caption">less</LessText>
        <Typography variant="caption">more</Typography>
      </Box>
      <Popper
        anchorEl={anchorEl}
        id="heatmap-chooser"
        open={!!anchorEl}
        sx={{zIndex: theme => theme.zIndex.modal + 1}}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <PopperPanel>
            {heatMapGradient.map((ramp, idx) => {
              return (
                <StyledBox
                  display="flex"
                  flexDirection="row"
                  key={ramp[0]}
                  onClick={selectRamp(idx)}
                  sx={{
                    backgroundColor:
                      ramp === gradient ? 'rgba(0,157,177,0.3)' : undefined,
                    padding: 1,
                  }}
                >
                  {ramp.map(color => {
                    return <LegendItem key={color} color={color} />;
                  })}
                </StyledBox>
              );
            })}
          </PopperPanel>
        </ClickAwayListener>
      </Popper>
    </LegendPanel>
  );
};

HeatMapLegend.propTypes = {
  gradient: PropTypes.array,
  onClick: PropTypes.func,
};

export default memo(HeatMapLegend);
